import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Central entitlement store for the frontend.
 *
 * Reads ``GET /api/subscription/me`` after auth, caches tier + features +
 * pricing, and exposes:
 *
 *   const { tier, features, has, isPro, isAdmin, refresh, upgrade,
 *           cancel, previewTier, pricing } = useSubscription();
 *   const canJournalInsights = useFeature("journal_insights");
 *
 * Never trust this state for gating on the backend — server-side
 * ``require_feature`` is the source of truth. This context is for UI.
 */
const SubscriptionContext = createContext(null);

const EMPTY = {
  tier: "free",
  features: [],
  status: null,
  renewal_at: null,
  cancelled_at: null,
  pro_since: null,
  preview_override: null,
  pricing: null,
  is_admin: false,
};

export function SubscriptionProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [showPricing, setShowPricing] = useState(false);
  const [pricingContext, setPricingContext] = useState(null); // { feature, title, blurb }

  const refresh = useCallback(async () => {
    if (!user) { setState(EMPTY); setLoading(false); return; }
    try {
      const { data } = await endpoints.subscriptionMe();
      setState(data);
    } catch {
      setState(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  const has = useCallback((feature) => state.features.includes(feature), [state.features]);
  const isPro = state.tier === "pro";

  const upgrade = useCallback(async (plan = "monthly") => {
    // Kick off Stripe Checkout. The frontend does not grant Pro — the
    // webhook + `/subscription/status/{session_id}` polling on the
    // success page do (see PricingSuccess.jsx).
    const { data } = await endpoints.subscriptionUpgrade(plan, window.location.origin);
    if (data?.checkout_url) {
      window.location.assign(data.checkout_url);
      return null;
    }
    // Backwards-compat if a non-Stripe backend responds with a snapshot.
    setState(data);
    setShowPricing(false);
    return data;
  }, []);

  const cancel = useCallback(async () => {
    const { data } = await endpoints.subscriptionCancel();
    setState(data);
    return data;
  }, []);

  const previewTier = useCallback(async (tier) => {
    await endpoints.adminPreviewTier(tier);
    await refresh();
  }, [refresh]);

  const openPricing = useCallback((ctx) => {
    setPricingContext(ctx || null);
    setShowPricing(true);
  }, []);
  const closePricing = useCallback(() => setShowPricing(false), []);

  return (
    <SubscriptionContext.Provider
      value={{
        ...state,
        loading,
        has,
        isPro,
        refresh,
        upgrade,
        cancel,
        previewTier,
        openPricing,
        closePricing,
        showPricing,
        pricingContext,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used inside SubscriptionProvider");
  return ctx;
}

/** Convenience hook for a single feature check. */
export function useFeature(feature) {
  const { has, loading } = useSubscription();
  return { entitled: has(feature), loading };
}
